// Manual usage entry, lifted out of the Bill Run detail so a bill run is only
// about comparing figures. It sits beside the NEM Analysis stepper as the other
// way usage can arrive: the stepper derives it from bv_nem_interval, this takes
// it from the operator.
//
// UI move only. The save path is unchanged — billService.updateUsage still
// writes onto one bv_bill row, so the period is whatever that bill carries and
// a bill has to be picked first. The period-keyed redesign (a manual entry that
// stands on its own and is matched to a bill run by date) is a separate piece of
// work; this deliberately does not pre-empt it.
import { useEffect, useState } from 'react';
import { Loader2, RefreshCw, X, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../foundation/altzero_base/base/components/ui/card';
import { Button } from '../../../foundation/altzero_base/base/components/ui/button';
import { supabase } from '../../../foundation/altzero_base/base/utils/supabaseClient';
import { billService } from '../services/billService';
import { calculateShadowBill } from '../services/shadowCalculationService';
import { EMPTY, billToForm, UsageFormFields } from './bills/SiteBillsTab';
import type { BvBill, BvBillFormData } from '../types/billTypes';

type Props = { siteId: string };

const fmtPeriod = (b: BvBill) => {
  const d = (s?: string | null) =>
    s ? new Date(s).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
  return `${d(b.bill_from)} – ${d(b.bill_to)}`;
};

export const ManualUsageEntry = ({ siteId }: Props) => {
  const [bills, setBills] = useState<BvBill[]>([]);
  const [billId, setBillId] = useState('');
  const [form, setForm] = useState<BvBillFormData>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        setLoading(true);
        const res = await billService.listBySite(siteId);
        if (!mounted) return;
        if (res.success) setBills(res.data ?? []);
        else setError(res.error ?? 'Failed to load bill periods');
      } catch (err) {
        if (mounted) setError(err instanceof Error ? err.message : 'Failed to load bill periods');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    void load();
    return () => { mounted = false; };
  }, [siteId]);

  // Typing a period that already has usage recorded loads it, so this edits the
  // existing figures rather than silently blanking them. Matching on the dates,
  // not on a picked row — the period is what the user is identifying.
  useEffect(() => {
    if (!form.bill_from || !form.bill_to) return;
    const match = bills.find(b => b.bill_from === form.bill_from && b.bill_to === form.bill_to);
    setBillId(match?.id ?? '');
    if (match) {
      const loaded = billToForm(match);
      // Keep what the user has typed for the dates; take the rest from the match.
      setForm(prev => ({ ...loaded, bill_from: prev.bill_from, bill_to: prev.bill_to }));
    }
    // Intentionally keyed on the dates alone: re-running when `form` changes
    // would clobber every keystroke in the kWh fields.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.bill_from, form.bill_to, bills]);

  const handleSave = async () => {
    if (!form.bill_from || !form.bill_to) { setError('Usage From and Usage To are required'); return; }
    if (form.bill_from >= form.bill_to) { setError('Usage To must be after Usage From'); return; }

    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      // The usage belongs to a period, not to a bill the user had to find first.
      // A period with no bill row yet gets one — that is bookkeeping, not a
      // decision the operator should have to make before typing numbers.
      let targetId = billId;
      if (!targetId) {
        const created = await billService.create({ ...EMPTY, site_id: siteId, bill_from: form.bill_from, bill_to: form.bill_to });
        if (!created.success || !created.data) {
          setError(created.error ?? 'Failed to create the bill period');
          return;
        }
        targetId = created.data.id;
        setBills(prev => [...prev, created.data as BvBill]);
        setBillId(targetId);
      }
      const res = await billService.updateUsage(targetId, form);
      if (!res.success || !res.data) { setError(res.error ?? 'Failed to save usage'); return; }
      let updated = res.data;
      try {
        const shadow = await calculateShadowBill(supabase, updated);
        const saved = await billService.saveShadowResult(updated.id, shadow);
        if (saved.success && saved.data) updated = saved.data;
      } catch {
        // Non-fatal: the usage is saved either way, and Recalculate is available
        // on the bill run. Swallowing it here matches the original behaviour.
      }
      setBills(prev => prev.map(b => (b.id === updated.id ? updated : b)));
      setSuccess('Usage saved & expected invoice recalculated');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save usage');
    } finally {
      setSaving(false);
    }
  };

  const matched = bills.find(b => b.id === billId);
  const matchedPeriod = matched ? fmtPeriod(matched) : null;

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-12 justify-center text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading bill periods…
      </div>
    );
  }

  return (
    <Card className="border-0 shadow-sm">
      <CardHeader className="pb-2 pt-4 px-5">
        <CardTitle className="text-sm font-semibold text-gray-700">Enter usage manually</CardTitle>
        <p className="text-xs text-muted-foreground mt-0.5">
          For periods with no interval data, or to override what the NEM Analysis produced.
          Saving recalculates the expected invoice from this site's contracts.
        </p>
      </CardHeader>
      <CardContent className="px-5 pb-5 space-y-4">
        {/* Usage From / Usage To are the first two fields of UsageFormFields, so
            the period is entered here like any other value — there is no bill to
            pick first. */}
        <UsageFormFields f={form} onChange={(field, value) => setForm(prev => ({ ...prev, [field]: value }))} />

        {matchedPeriod && (
          <p className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
            An existing entry for {matchedPeriod} was loaded — saving updates it.
          </p>
        )}

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 flex items-center gap-1.5">
            <X className="h-3.5 w-3.5 flex-shrink-0" />{error}
          </div>
        )}
        {success && (
          <div className="rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-xs text-green-800 flex items-center gap-1.5">
            <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0" />{success}
          </div>
        )}

        <Button size="sm" onClick={() => void handleSave()} disabled={saving} className="gap-1.5">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          {saving ? 'Saving…' : 'Save Usage'}
        </Button>
      </CardContent>
    </Card>
  );
};
